import { Injectable, NgZone } from '@angular/core';
import type { Chart, ChartConfiguration, ChartType } from 'chart.js';

/**
 * Service for rendering Chart.js charts with proper NgZone handling.
 * Ensures charts render outside Angular zone for performance,
 * but triggers change detection when complete.
 *
 * Chart.js is loaded dynamically via import() so it stays out of the
 * initial bundle. The module is fetched once and cached; subsequent
 * createChart calls are synchronous after the first load.
 */
@Injectable({ providedIn: 'root' })
export class ChartRenderService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ChartClass: (new (...args: any[]) => Chart) | null = null;
  private loadPromise: Promise<void> | null = null;

  constructor(private ngZone: NgZone) {
    // Kick off the dynamic load immediately so chart.js is ready
    // before the first chart render, without blocking the initial bundle.
    this.preload();
  }

  /**
   * Starts the dynamic import of chart.js. Safe to call multiple times.
   */
  preload(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = import('chart.js').then((m) => {
        m.Chart.register(...m.registerables);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.ChartClass = m.Chart as any;
      });
    }
    return this.loadPromise;
  }

  /**
   * Renders a callback after view initialization with proper zone handling.
   * Use this for any chart rendering that uses setTimeout.
   * @param callback Function to execute after delay
   * @param delay Delay in milliseconds (default: 100)
   */
  renderAfterViewInit(callback: () => void, delay: number = 100): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(callback);
      }, delay);
    });
  }

  /**
   * Creates a Chart.js chart with proper zone handling.
   * Renders outside Angular zone for better performance.
   *
   * chart.js is loaded dynamically on first service use. If called before
   * the dynamic import has resolved, this method awaits it internally.
   * Callers that already await renderAfterViewInit are safe; otherwise
   * wrap in `await this.chartRenderService.preload()` before calling.
   *
   * @param canvas Canvas element or context
   * @param config Chart configuration
   * @returns Chart instance
   */
  createChart<T extends ChartType>(
    canvas: HTMLCanvasElement | CanvasRenderingContext2D,
    config: ChartConfiguration<T>
  ): Chart<T> {
    if (!this.ChartClass) {
      throw new Error(
        'ChartRenderService: chart.js not loaded yet. ' +
        'Await preload() before creating a chart.'
      );
    }
    const ChartClass = this.ChartClass;
    let chart: Chart<T>;

    this.ngZone.runOutsideAngular(() => {
      chart = new ChartClass(canvas, config) as Chart<T>;
    });

    return chart!;
  }

  /**
   * Updates an existing chart with proper zone handling.
   * @param chart Chart instance to update
   * @param updateFn Function that modifies the chart
   */
  updateChart(chart: Chart, updateFn: (chart: Chart) => void): void {
    this.ngZone.runOutsideAngular(() => {
      updateFn(chart);
      chart.update();
    });
  }

  /**
   * Destroys a chart safely.
   * @param chart Chart instance to destroy
   */
  destroyChart(chart: Chart | null | undefined): void {
    if (chart) {
      this.ngZone.runOutsideAngular(() => {
        chart.destroy();
      });
    }
  }

  /**
   * Executes a function outside Angular zone (for performance).
   * Use for heavy computations that don't need UI updates.
   * @param callback Function to execute
   */
  runOutsideAngular<T>(callback: () => T): T {
    return this.ngZone.runOutsideAngular(callback);
  }

  /**
   * Executes a function inside Angular zone (triggers change detection).
   * @param callback Function to execute
   */
  runInsideAngular<T>(callback: () => T): T {
    return this.ngZone.run(callback);
  }
}
